import { baseComboType } from '#basecombo/mode';
import { delistingReasonScrapped, delistingType } from '#delisting/constants';
import { detailsType } from '#details/constants';
import { DetailContext } from '#submission/context';
import { baseTypes, Submission, SubmissionType } from '#submission/model';
import { RecordIssue } from '#submission/validator';
import { priorTrxauthzType } from '#trxauth.prior/constants';
import { useVesselOracle } from '#vessel/oracle';
import { useIOTCNumber } from '#vid/iotcnumber';
import { useVID } from '#vid/model';
import { utils } from 'apprise-core/utils';
import { useT } from 'apprise-frontend-core/intl/language';
import { useL } from 'apprise-frontend-core/intl/multilang';
import { useTenantStore } from 'apprise-frontend-iam/tenant/store';
import { useContext } from 'react';
import { GenericRecord, isDelisted, RavRecord, RecordIdentifier, scopeOf, useRavRecordMissingInfo } from './model';
import { useRecordOracle } from './oracle';
import { useRecordPlugin } from './plugin';
import { avlType } from '#avl/constants';


export type RecordValidationData<T extends GenericRecord> = {

    patch: T
    current?: RavRecord

    record?: RavRecord

}

// the parsing contract: take an file and extract records for a submission.
export type RecordValidator<T extends GenericRecord> = (data: RecordValidationData<T>[], submission: Submission<T>) => Record<RecordIdentifier, RecordIssue[]>

export const useGenericValidator = <T extends GenericRecord>(type: SubmissionType, validator: RecordValidator<T>): RecordValidator<T> => {

    const t = useT()
    const l = useL()
    const tenants = useTenantStore()
    const vids = useVID()
    const ext = useIOTCNumber()

    const ravRecordMissingInfo = useRavRecordMissingInfo()

    const { sameRecords } = useContext(DetailContext)

    const vessels = useVesselOracle()

    const oracle = useRecordOracle()

    const { allowedScopes, allowThirdPartyFor, canClaim } = useRecordPlugin(type)

    const needsCurrentValidation = ![baseComboType, detailsType, delistingType, priorTrxauthzType].includes(type) && type!==avlType

    return (data, submission) => {

        const uvimap = utils().index(data.map(d => d.patch)).byGroup(t => t.uvi)

        const { matchStatusOf, isExcluded } = oracle.given(submission)

        const generic = data.reduce((acc, { patch, current }) => {

            const issues: RecordIssue[] = []

            const currentscope = scopeOf(current ?? patch)

            if (sameRecords.includes(patch.id))
                issues.push({ message: t('valid.unchanged_patch'), type: 'warning' })

            const currentlyDelisted = isDelisted(current)

            if (currentlyDelisted && current?.[delistingType]?.reason === delistingReasonScrapped && type !== avlType) {

                issues.push({ message: t('valid.scrapped_vessel'), type: 'error', location: 'uvi' })

            }

            // authz is required to claim (unless the patch is a delisting in itself)
            if (current && currentlyDelisted && submission.type !== delistingType && submission.type !== avlType && !patch.authorization)
                issues.push({ message: t('valid.claim_noauthz', { uvi: vids.valueOf(current.uvi) }), type: 'error', location: 'uvi' })

            const allowThirdPartyTest = allowThirdPartyFor({ ...patch, current })
           
            const [allowThirdParty, severity = 'error'] = Array.isArray(allowThirdPartyTest) ? allowThirdPartyTest : [allowThirdPartyTest]

            // third pary claims can't occur unless submission allows it and  vessel allow it.
            if (!allowThirdParty)
                if (current && current.tenant !== patch.tenant && !(canClaim({ ...patch, current }) && vessels.canClaim(current)))
                    issues.push({ message: t('valid.otherflag_uvi', { uvi: vids.valueOf(current.uvi), state: l(tenants.safeLookup(current.tenant)?.name) }), type: severity, location: 'uvi' })


            if (matchStatusOf(patch) === 'pending' && patch.lifecycle.state !== 'ignored')
                issues.push({ message: t('valid.unmatched_uvi'), type: 'error', location: 'uvi' })

            if (!baseTypes.includes(type) && !patch.uvi)
                issues.push({ message: t('valid.missing_uvi'), type: 'error', location: 'uvi' })

            if (patch.uvi && !ext.isValidPrimaryUvi(vids.parse(patch.uvi).value!))
                issues.push({ message: t('valid.invalid_uvi', { uvi: vids.valueOf(patch.uvi) }), type: 'error', location: 'uvi' })

            if (patch.uvi && ext.isValidPrimaryUvi(vids.parse(patch.uvi).value!) && !current?.uvi)
                issues.push({ message: t('valid.unknown_uvi', { uvi: vids.valueOf(patch.uvi) }), type: 'error', location: 'uvi' })

            if (patch.uvi && uvimap[patch.uvi].filter(patch => !isExcluded(patch)).length > 1)
                issues.push({ message: t('valid.duplicate_uvi', { uvi: vids.valueOf(patch.uvi), length: uvimap[patch.uvi].length }), type: 'error', location: 'uvi' })

            if (patch.uvi && !allowedScopes.includes(currentscope))
                issues.push({ message: t(`valid.invalid_scope_${currentscope}`, { uvi: vids.valueOf(patch.uvi), currentscope: t(`valid.scope_${currentscope}`) }), type: 'error', location: 'uvi' })
            else if (patch.scope !== undefined && currentscope !== patch.scope)
                issues.push({ message: t(`valid.change_scope`, { uvi: vids.valueOf(patch.uvi), fromscope: t(`valid.scope_${currentscope}`), toscope: t(`valid.scope_${patch.scope}`) }), type: 'warning', location: 'uvi' })


            if (current && needsCurrentValidation) {
                const currentMissingInfo = ravRecordMissingInfo(current)
                if (currentMissingInfo.length)
                    issues.push({ message: t('valid.missing_details', { errors: currentMissingInfo.map(miss => miss.message).join(', ') }), type: 'error', location: 'details' })
            }

            return { ...acc, [patch.id]: issues }

        }, {})

        return Object.entries(validator(data, submission)).reduce((acc, [id, issues]) => ({

            ...acc,
            [id]: [...(acc[id] ?? []), ...issues]

        }), generic)
    }
}
